/**
 * Profil — page profil unifiée pour pasteurs et fidèles.
 *
 * Pour TOUS : photo, infos personnelles, bio, sécurité (mot de passe).
 * Pour FIDÈLES en plus : contributions, présences, services bénévoles, annonces.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import useAuth from "../hooks/useAuth";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";
import { authApi, benevolesApi, donsApi, presencesApi } from "../services/api";
import Card from "../components/Card";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Input from "../components/Input";

const TITRES = ["Pasteur", "Évêque", "Ancien", "Diacre", "Responsable", "Membre"];

export default function Profil() {
  const { user, isAdmin, updateUser, usingMock } = useAuth();
  const { members, eglises } = useMembers();
  const toast = useToast();

  const memberInfo = members.find((m) => m.id === user?.member_id);
  const eglise = eglises.find((e) => e.id === user?.eglise_id);

  // ─── Form profil ───
  const [form, setForm] = useState({
    full_name: "",
    titre: "",
    email: "",
    telephone: "",
    bio: "",
    ville: "",
    pays: "",
    date_naissance: "",
    adresse: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      full_name: user.full_name || "",
      titre: user.titre || "",
      email: user.email || "",
      telephone: user.telephone || "",
      bio: user.bio || "",
      ville: user.ville || "",
      pays: user.pays || "",
      date_naissance: user.date_naissance || "",
      adresse: user.adresse || "",
    });
  }, [user]);

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) {
      toast.error("Le nom complet est obligatoire");
      return;
    }
    setSavingProfile(true);
    try {
      if (usingMock) {
        updateUser(form);
        toast.success("✓ Profil mis à jour (mode démo)");
        return;
      }
      const { data } = await authApi.updateMe({
        full_name: form.full_name.trim(),
        email: form.email.trim() || null,
        telephone: form.telephone.trim() || null,
        titre: form.titre.trim() || null,
        bio: form.bio.trim() || null,
        ville: form.ville.trim() || null,
        pays: form.pays.trim() || null,
        adresse: form.adresse.trim() || null,
        date_naissance: form.date_naissance || null,
      });
      updateUser(data);
      toast.success("✓ Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingProfile(false);
    }
  };

  // ─── Upload photo ───
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (5 Mo max)");
      return;
    }
    setUploading(true);
    try {
      if (usingMock) {
        const reader = new FileReader();
        reader.onload = () => { updateUser({ photo_url: reader.result }); toast.success("✓ Photo mise à jour (démo)"); setUploading(false); };
        reader.readAsDataURL(file);
        return;
      }
      const { data } = await authApi.uploadPhoto(file);
      updateUser(data);
      toast.success("✓ Photo mise à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!user?.photo_url) return;
    try {
      if (usingMock) { updateUser({ photo_url: null }); toast.success("Photo supprimée (démo)"); return; }
      const { data } = await authApi.removePhoto();
      updateUser(data);
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // ─── Mot de passe ───
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) { toast.error("Le nouveau mot de passe doit faire 8 caractères minimum"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setPwdSubmitting(true);
    try {
      if (usingMock) { toast.success("✓ Mot de passe modifié (démo)"); setPwd({ current: "", next: "", confirm: "" }); return; }
      await authApi.changePassword(pwd.current, pwd.next);
      toast.success("✓ Mot de passe modifié");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe actuel incorrect");
    } finally {
      setPwdSubmitting(false);
    }
  };

  // ─── Données fidèle (chargées seulement si non admin) ───
  const [donsStats, setDonsStats] = useState(null);
  const [presenceStats, setPresenceStats] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (isAdmin || !user?.member_id) return;
    let cancelled = false;
    donsApi.list({ member_id: user.member_id, per_page: 100 })
      .then(({ data }) => { if (!cancelled) setDonsStats(computeDonsStats(data?.items || data || [])); })
      .catch(() => {});
    presencesApi.membreStats(user.member_id, 3)
      .then(({ data }) => { if (!cancelled) setPresenceStats(data); })
      .catch(() => {});
    benevolesApi.listPlanning()
      .then(({ data }) => { if (!cancelled) setServices((data || []).filter((s) => s.member_id === user.member_id)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, user?.member_id]);

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : null;

  const initials = (form.full_name || user?.full_name || "?")
    .split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  // Court-circuit : fidèle en attente de validation → écran d'attente bienveillant
  if (!isAdmin && user?.statut_inscription === "en_attente") {
    return <EnAttenteScreen user={user} eglise={eglise} />;
  }

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text, display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Hero — photo + nom + rôle */}
      <Card style={{
        background: `linear-gradient(135deg, ${colors.goldDim} 0%, ${colors.card} 100%)`,
        border: `1px solid ${colors.goldBorder}`,
        padding: 32,
      }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
          <PhotoBlock
            photoUrl={user?.photo_url}
            initials={initials}
            uploading={uploading}
            onPick={() => fileInputRef.current?.click()}
            onRemove={removePhoto}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPickPhoto}
            style={{ display: "none" }}
          />
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ fontFamily: fonts.heading, fontSize: 36, fontWeight: 700, marginBottom: 4 }}>
              {user?.titre ? `${user.titre} ${user?.full_name}` : user?.full_name}
            </h1>
            <div style={{ color: colors.textMuted, fontSize: 14, marginBottom: 8 }}>{user?.email}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Badge variant={isAdmin ? "gold" : "info"} dot>{user?.role}</Badge>
              {eglise && <Badge variant="info">⛪ {eglise.nom}</Badge>}
              {memberInfo?.departement && <Badge variant="success">{memberInfo.departement}</Badge>}
            </div>
            {memberSince && (
              <div style={{ marginTop: 10, color: colors.textMuted, fontSize: 13 }}>
                Membre depuis : {memberSince}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Informations personnelles */}
      <Card>
        <h2 style={sectionTitle}>Informations personnelles</h2>
        <form onSubmit={saveProfile} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Grid>
            <Input label="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
            <SelectField
              label="Titre"
              value={form.titre}
              onChange={(v) => setForm({ ...form, titre: v })}
              options={[{ value: "", label: "Aucun" }, ...TITRES.map((t) => ({ value: t, label: t }))]}
            />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Input label="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+33 6 00 00 00 00" />
            <Input label="Date de naissance" type="date" value={form.date_naissance || ""} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} />
            <Input label="Adresse" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
            <Input label="Ville" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
            <Input label="Pays" value={form.pays} onChange={(e) => setForm({ ...form, pays: e.target.value })} />
          </Grid>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: colors.textMuted, marginBottom: 6, display: "block" }}>
              Biographie
            </label>
            <textarea
              rows={4}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Quelques mots sur vous, votre ministère…"
              style={textareaStyle}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" disabled={savingProfile}>
              {savingProfile ? "Enregistrement…" : "💾 Sauvegarder les modifications"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Sécurité */}
      <Card>
        <h2 style={sectionTitle}>Sécurité</h2>
        <form onSubmit={submitPassword} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Grid>
            <Input label="Mot de passe actuel" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} required />
            <Input label="Nouveau mot de passe" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} required />
            <Input label="Confirmer" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required />
          </Grid>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="ghost" disabled={pwdSubmitting}>
              {pwdSubmitting ? "Modification…" : "🔒 Changer le mot de passe"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Sections fidèle */}
      {!isAdmin && donsStats && (
        <Card>
          <h2 style={sectionTitle}>💰 Mes contributions</h2>
          <Grid>
            <StatField label="Total cette année" value={donsStats.totalYear} />
            <StatField label="Dernier don" value={donsStats.lastDonLabel || "—"} />
            <StatField label="Nombre de dons" value={donsStats.count.toString()} />
          </Grid>
          <div style={{ marginTop: 16 }}>
            <Link to="/finances" style={{ color: colors.gold, fontWeight: 600, fontSize: 13 }}>
              Voir mon historique complet →
            </Link>
          </div>
        </Card>
      )}

      {!isAdmin && presenceStats && (
        <Card>
          <h2 style={sectionTitle}>✓ Mes présences</h2>
          <Grid>
            <StatField label="Taux de présence (3 mois)" value={presenceStats.total_cultes ? `${presenceStats.taux}%` : "—"} />
            <StatField
              label="Dernière présence"
              value={presenceStats.derniere_presence
                ? new Date(presenceStats.derniere_presence).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })
                : "—"}
            />
          </Grid>
          {presenceStats.total_cultes > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 8, background: colors.bg, borderRadius: 999, overflow: "hidden", border: `1px solid ${colors.border}` }}>
                <div style={{
                  height: "100%",
                  width: `${presenceStats.taux}%`,
                  background: presenceStats.taux >= 70 ? colors.green : presenceStats.taux >= 40 ? colors.gold : colors.rose,
                  transition: "width 300ms",
                }} />
              </div>
            </div>
          )}
        </Card>
      )}

      {!isAdmin && services.length > 0 && (
        <Card>
          <h2 style={sectionTitle}>🤝 Mes services</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {services.map((s) => (
              <li key={s.id} style={{
                padding: 12,
                background: colors.bg,
                borderRadius: 10,
                borderLeft: `3px solid ${s.equipe_couleur || colors.gold}`,
                fontSize: 14,
              }}>
                <strong>{s.evenement_titre || `Service`}</strong>
                <span style={{ color: colors.textMuted, marginLeft: 8 }}>
                  · {new Date(s.date_service).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "short" })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function PhotoBlock({ photoUrl, initials, uploading, onPick, onRemove }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 100, height: 100, borderRadius: "50%",
        background: photoUrl ? `center/cover no-repeat url(${photoUrl})` : "linear-gradient(135deg, #E4A72C, #F5C55A)",
        border: `3px solid ${colors.gold}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#0D1117", fontWeight: 700, fontSize: 32, fontFamily: "Plus Jakarta Sans",
      }}>
        {!photoUrl && initials}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          style={{
            background: colors.gold, color: "#050709",
            border: "none", borderRadius: 8, padding: "8px 14px",
            fontSize: 12, fontWeight: 600, cursor: uploading ? "wait" : "pointer",
            opacity: uploading ? 0.7 : 1,
          }}
        >
          {uploading ? "Envoi…" : "📷 Changer la photo"}
        </button>
        {photoUrl && (
          <button
            type="button"
            onClick={onRemove}
            style={{ background: "transparent", color: colors.rose, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Retirer
          </button>
        )}
      </div>
    </div>
  );
}

function Grid({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
      {children}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: colors.textMuted }}>{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: colors.bg, color: colors.text,
          border: `1px solid ${colors.border}`, borderRadius: 10,
          padding: "12px 14px", fontSize: 14, outline: "none",
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function StatField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: 24, fontWeight: 700, color: colors.gold }}>{value}</div>
    </div>
  );
}

function computeDonsStats(items) {
  const year = new Date().getFullYear();
  let totalYear = 0;
  let last = null;
  for (const d of items) {
    const dt = new Date(d.date_don);
    if (dt.getFullYear() === year) totalYear += d.montant || 0;
    if (!last || dt > new Date(last.date_don)) last = d;
  }
  const fmt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
  return {
    totalYear: `${fmt.format(totalYear)} ${items[0]?.devise || "EUR"}`,
    lastDonLabel: last ? new Date(last.date_don).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : null,
    count: items.length,
  };
}

const sectionTitle = {
  fontFamily: fonts.heading,
  fontSize: 22,
  fontWeight: 700,
  marginBottom: 16,
};

const textareaStyle = {
  width: "100%",
  background: colors.bg,
  color: colors.text,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 14,
  fontFamily: "Plus Jakarta Sans, system-ui, sans-serif",
  outline: "none",
  resize: "vertical",
};

// ─── Écran d'attente pour les fidèles non encore validés ─────────────
function EnAttenteScreen({ user, eglise }) {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      fontFamily: fonts.body,
    }}>
      <div style={{
        maxWidth: 560,
        width: "100%",
        background: `linear-gradient(135deg, ${colors.goldDim} 0%, ${colors.card} 100%)`,
        border: `1px solid ${colors.goldBorder}`,
        borderRadius: 18,
        padding: "40px 32px",
        textAlign: "center",
        boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
      }}>
        <div style={{ fontSize: 48, color: colors.gold, marginBottom: 14 }}>✝</div>
        <h1 style={{ fontFamily: fonts.heading, fontSize: 30, fontWeight: 700, marginBottom: 12 }}>
          Bienvenue sur Mon Ministère, {user?.full_name?.split(" ")[0] || "ami"} !
        </h1>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 14px",
          background: colors.bg,
          border: `1px solid ${colors.goldBorder}`,
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          color: colors.gold,
          marginBottom: 20,
        }}>
          🙏 Validation en cours
        </div>
        <p style={{ color: colors.text, fontSize: 15, lineHeight: 1.6, marginBottom: 16 }}>
          Votre inscription{eglise ? <> à <strong>{eglise.nom}</strong></> : null} est en cours
          de validation par votre pasteur.
        </p>
        <p style={{ color: colors.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
          Vous recevrez une confirmation dès que votre compte sera activé. En attendant,
          vous pouvez explorer les événements à venir.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/evenements" style={{ textDecoration: "none" }}>
            <Button>📅 Voir les événements →</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
